import type { Storey } from '../props';
import type { BuildStore } from '../types';

// A parapet ring counts as a storey but is nothing to copy: the Duplex's "Roof" carries four
// wall segments and no slab, Level 2 carries twenty-five. Five is the line between the two.
export const INHABITED_WALLS = 4;
const FALLBACK_HEIGHT = 3;
export const SAME_LEVEL = 0.01;

export const elevationOf = (s: Storey): number => s.elevation ?? 0;

export async function orderedStoreys(store: BuildStore): Promise<Storey[]> {
  const storeys = await store.storeys();
  return [...storeys].sort((a, b) => elevationOf(a) - elevationOf(b));
}

// The storey a recipe reads from: the caller's, or the highest one that is a floor rather than
// a parapet. Walked from the top so the answer does not depend on storey names.
export async function sourceStorey(store: BuildStore, name?: string): Promise<Storey> {
  const storeys = await orderedStoreys(store);
  if (!storeys.length) throw new Error('the model has no storey');
  if (name) {
    const wanted = name.trim().toLowerCase();
    const hit = storeys.find((s) => s.name.toLowerCase() === wanted);
    if (!hit) throw new Error(`no storey named ${name}`);
    return hit;
  }
  for (let i = storeys.length - 1; i >= 0; i--) {
    const segments = await store.wallSegments(storeys[i]!.expressID);
    if (segments.length > INHABITED_WALLS) return storeys[i]!;
  }
  return storeys[storeys.length - 1]!;
}

// Floor to floor: the gap to the storey above, or the one below when the source is the top.
// Storeys sharing an elevation do not count as a gap; a new storey lands on the old roof level.
export function storeyHeight(storeys: Storey[], storey: Storey): number {
  const here = elevationOf(storey);
  const above = storeys.find((s) => elevationOf(s) > here + SAME_LEVEL);
  if (above) return round(elevationOf(above) - here);
  const below = [...storeys].reverse().find((s) => elevationOf(s) < here - SAME_LEVEL);
  if (below) return round(here - elevationOf(below));
  return FALLBACK_HEIGHT;
}

// The storeys a new one lands on: whatever sits between its datum and its ceiling, the
// Duplex's parapet ring and roof terrace for one. They make way, contents and all.
export function storeysInTheWay(storeys: Storey[], elevation: number, height: number): Storey[] {
  return storeys.filter((s) => {
    const at = elevationOf(s);
    return at >= elevation - SAME_LEVEL && at < elevation + height - SAME_LEVEL;
  });
}

// "Level 2" -> "Level 3"; anything without a trailing number just gets one.
export function nextStoreyName(name: string): string {
  const m = name.match(/^(.*?)(\d+)(\D*)$/);
  if (!m) return `${name} 2`;
  return `${m[1]}${Number(m[2]) + 1}${m[3]}`;
}

const round = (v: number) => Number(v.toFixed(6));
